import { TheContent, TheFooter, TheHeader, TheSidebar } from "./index";
import { useEffect, useState } from "react";

import { useLocation } from "react-router-dom";
import { useNavSidebarStore } from "src/stores/NavSidebarStore";
import { useResponsive } from "src/reusable/hooks/useResponsive";
import { useMinimalUI } from "src/reusable/hooks/useMobileApp";
import {
  GlobalHeaderHelpPill,
  HelpDrawer,
  HelpDrawerProvider,
} from "src/components/help";

// Breakpoint for sidebar visibility (matches MUI md breakpoint: 900px)
const SIDEBAR_BREAKPOINT = 900;

const TheLayout = () => {
  const location = useLocation();
  const [showSidebar, setShowSidebar] = useState(false);
  // Module-list collapse is persisted across reloads — see NavSidebarStore.
  const collapsed = useNavSidebarStore((s) => s.collapsed);
  const setCollapsed = useNavSidebarStore((s) => s.setCollapsed);
  const [isLargeScreenState, setIsLargeScreenState] = useState(
    window.matchMedia(`(min-width: ${SIDEBAR_BREAKPOINT}px)`).matches,
  );
  const { isMobile } = useResponsive();
  const isMinimalUI = useMinimalUI(); // True when embedded in mobile app WebView

  // Check if screen is large enough for persistent sidebar
  const isLargeScreen = () =>
    window.matchMedia(`(min-width: ${SIDEBAR_BREAKPOINT}px)`).matches;

  // Add effect to update isLargeScreenState when window resizes
  useEffect(() => {
    // on init: check if sidebar should show
    setShowSidebar(isLargeScreen());

    const handleResize = () => {
      setIsLargeScreenState(isLargeScreen());
      setShowSidebar(isLargeScreen());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Ensures the nav bar closes on smaller screens when navigating
  useEffect(() => {
    if (!isLargeScreen()) {
      setShowSidebar(false);
    }
  }, [location]);

  const calcSideBarWidth = () => {
    if (!showSidebar) {
      return 0;
    } else {
      return collapsed ? 60 : 220;
    }
  };

  const toggleSidebar = () => {
    setShowSidebar((prevShowSidebar) => !prevShowSidebar);
  };

  const shouldRenderFooter = () => {
    if (isMobile) {
      return false;
    }

    const { pathname } = location;
    return !(
      pathname.startsWith("/products/cap-ai/auto") && pathname.endsWith("/flow")
    );
  };

  // When in WebView (minimal UI), sidebar is always hidden
  const effectiveSideBarWidth = isMinimalUI ? 0 : calcSideBarWidth();

  return (
    <HelpDrawerProvider>
      <div className="c-app c-default-layout">
        {/* Hide sidebar completely when embedded in mobile app WebView */}
        {!isMinimalUI && (
          <TheSidebar
            isLargeScreen={isLargeScreenState}
            showSidebar={showSidebar}
            setShowSidebar={toggleSidebar}
            sideBarWidth={effectiveSideBarWidth}
            collapsed={collapsed}
            setCollapsed={setCollapsed}
          />
        )}
        <div className="c-wrapper">
          {/* Hide header when embedded in mobile app WebView */}
          {!isMinimalUI && (
            <TheHeader
              isLargeScreen={isLargeScreen}
              showSidebar={showSidebar}
              setShowSidebar={setShowSidebar}
            />
          )}
          <div className="c-body" style={{ zIndex: "100" }}>
            <TheContent />
          </div>

          {shouldRenderFooter() && !isMinimalUI && (
            <TheFooter sideBarWidth={effectiveSideBarWidth} />
          )}
        </div>
        <HelpDrawer />
        {/* Portals the "How to use X" pill into the global header beside the
            breadcrumb, so the header chrome owns the pill without TheHeader
            having to render it. Route-gated internally. */}
        <GlobalHeaderHelpPill />
      </div>
    </HelpDrawerProvider>
  );
};

export default TheLayout;
